import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/header.scss";

const shopColumns = [
  {
    title: "CLOTHING",
    links: [
      "Dresses",
      "Tops & blouses",
      "Pants & shorts",
      "Sweaters & cardigans",
      "Coats & jackets",
      "Denim",
      "T-shirts & sweatshirts",
      "Skirts",
    ],
  },
  {
    title: "ACCESSORIES",
    links: ["Bags", "Shoes"],
  },
  {
    title: "COLLECTIONS",
    links: [
      "Seasonal edit",
      "Occasion dressing",
      "Top-handle bags",
      "New arrivals",
    ],
  },
];

function Header() {
  const { cartItems, cartCount, wishlistItems, removeFromCart } =
    useContext(CartContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState("root");
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [query, setQuery] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isMinimal = ["/checkout", "/paysuccess"].includes(location.pathname);

  useEffect(() => {
    setMobileOpen(false);
    setMobilePanel("root");
    setSearchOpen(false);
    setAccountOpen(false);
    setCartOpen(false);
    window.requestAnimationFrame(() => {
      if (location.hash) {
        document.getElementById(location.hash.slice(1))?.scrollIntoView();
      } else {
        window.scrollTo(0, 0);
      }
    });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    document.body.classList.toggle(
      "nav-open",
      mobileOpen || searchOpen || cartOpen,
    );
    return () => document.body.classList.remove("nav-open");
  }, [mobileOpen, searchOpen, cartOpen]);

  useEffect(() => {
    if (isMinimal) return undefined;
    const handleScroll = () => setCompact(window.scrollY > 60);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMinimal]);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const nextQuery = query.trim();
    navigate(nextQuery ? `/shop?q=${encodeURIComponent(nextQuery)}` : "/shop");
  };

  const logo = (
    <Link
      className="wordmark"
      to="/"
      aria-label={`${siteConfig.displayName} home`}
    >
      <img src={siteConfig.logo.image} alt={siteConfig.displayName} />
    </Link>
  );

  const detailedCart = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === item.id),
    }))
    .filter((item) => item.product);
  const cartSubtotal = detailedCart.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0,
  );

  if (isMinimal) {
    return (
      <header className="site-header site-header--minimal">
        <div className="minimal-header__inner">{logo}</div>
      </header>
    );
  }

  return (
    <>
      <header className={`site-header ${compact ? "is-compact" : ""}`}>
        <Link className="announcement" to="/returns">
          {siteConfig.announcement}
        </Link>

        <div className="header-bar">
          <button
            className="icon-button mobile-menu-button"
            onClick={() => {
              setMobilePanel("root");
              setMobileOpen(true);
            }}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
          >
            <FiMenu />
          </button>

          <nav className="desktop-nav" aria-label="Primary navigation">
            <div className="desktop-nav__item desktop-nav__item--mega">
              <Link to="/shop?sort=new">New in</Link>
              <div className="mega-menu" aria-label="New in categories">
                <div className="mega-menu__links">
                  {shopColumns.slice(0, 2).map((column) => (
                    <div key={column.title}>
                      <p>{column.title}</p>
                      {column.links.map((link) => (
                        <Link
                          key={link}
                          to={`/shop?category=${encodeURIComponent(link)}`}
                        >
                          {link}
                        </Link>
                      ))}
                      <Link to="/shop">View all</Link>
                    </div>
                  ))}
                </div>
                <Link className="mega-menu__campaign" to="/shop?sort=new">
                  <img
                    src="/img/generated/product-14-view-06.jpg"
                    alt="New season color story"
                  />
                  <span>Seasonal edit</span>
                  <u>Shop now</u>
                </Link>
              </div>
            </div>

            <div className="desktop-nav__item desktop-nav__item--mega">
              <Link to="/shop">Shop</Link>
              <div
                className="mega-menu mega-menu--wide"
                aria-label="Shop categories"
              >
                <div className="mega-menu__links">
                  {shopColumns.map((column) => (
                    <div key={column.title}>
                      <p>{column.title}</p>
                      {column.links.map((link) => (
                        <Link
                          key={link}
                          to={
                            column.title === "COLLECTIONS"
                              ? "/shop?sort=new"
                              : `/shop?category=${encodeURIComponent(link)}`
                          }
                        >
                          {link}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
                <Link className="mega-menu__campaign" to="/shop">
                  <img
                    src="/img/generated/product-19-view-06.jpg"
                    alt="Fall collection editorial"
                  />
                  <span>New collection</span>
                  <u>Explore</u>
                </Link>
              </div>
            </div>
            <Link to="/about">Stories</Link>
            <Link to="/shop?sort=price-asc">Last call</Link>
            <div className="desktop-nav__item desktop-nav__item--more">
              <button type="button">
                More <FiChevronDown />
              </button>
              <div className="more-menu">
                <Link to="/faqs">FAQs</Link>
                <Link to="/privacy">Privacy policy</Link>
                <Link to="/cookies">Cookie policy</Link>
                <Link to="/terms">Terms of use</Link>
              </div>
            </div>
          </nav>

          {logo}

          <div className="header-actions">
            <button
              className="icon-button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <FiSearch />
            </button>
            <div className="account-wrap">
              <button
                className="icon-button account-button"
                onClick={() => setAccountOpen((open) => !open)}
                aria-label="Account"
                aria-expanded={accountOpen}
              >
                <FiUser />
              </button>
              {accountOpen && (
                <div className="account-popover">
                  <strong>Welcome</strong>
                  <p>Your local wishlist and bag are saved on this device.</p>
                  <button onClick={() => setAccountOpen(false)}>
                    Continue as guest
                  </button>
                </div>
              )}
            </div>
            <Link
              className="icon-button desktop-only-action"
              to="/shop?wishlist=true"
              aria-label={`Wishlist with ${wishlistItems.length} items`}
            >
              <FiHeart />
              {wishlistItems.length > 0 && <span>{wishlistItems.length}</span>}
            </Link>
            <button
              className="icon-button"
              onClick={() => setCartOpen(true)}
              aria-label={`Shopping bag with ${cartCount} items`}
              aria-expanded={cartOpen}
            >
              <FiShoppingBag />
              <span>{cartCount}</span>
            </button>
          </div>
        </div>
      </header>

      <div
        className={`drawer-overlay ${mobileOpen ? "is-open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      <aside className={`mobile-drawer ${mobileOpen ? "is-open" : ""}`}>
        <div className="mobile-drawer__header">
          <strong>Menu</strong>
          <button
            className="icon-button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>
        <div className="mobile-drawer__panels">
          <div
            className={`mobile-drawer__track ${mobilePanel === "shop" ? "show-shop" : ""}`}
          >
            <nav aria-label="Mobile navigation">
              <Link to="/shop?sort=new">New in</Link>
              <button type="button" onClick={() => setMobilePanel("shop")}>
                Shop <FiChevronRight />
              </button>
              <Link to="/about">Stories</Link>
              <Link to="/shop?sort=price-asc">Last call</Link>
              <Link to="/shop?wishlist=true">
                Wishlist ({wishlistItems.length})
              </Link>
              <Link to="/faqs">FAQs</Link>
              <Link to="/privacy">Privacy policy</Link>
              <Link to="/cookies">Cookie policy</Link>
              <Link to="/terms">Terms of use</Link>
            </nav>
            <nav aria-label="Shop categories">
              <button
                className="mobile-drawer__back"
                type="button"
                onClick={() => setMobilePanel("root")}
              >
                <FiChevronLeft /> Back
              </button>
              <Link to="/shop">Shop all</Link>
              <p>CLOTHING</p>
              {shopColumns[0].links.map((link) => (
                <Link
                  key={link}
                  to={`/shop?category=${encodeURIComponent(link)}`}
                >
                  {link}
                </Link>
              ))}
              <p>ACCESSORIES</p>
              {shopColumns[1].links.map((link) => (
                <Link
                  key={link}
                  to={`/shop?category=${encodeURIComponent(link)}`}
                >
                  {link}
                </Link>
              ))}
              <p>COLLECTIONS</p>
              <Link to="/shop?sort=new">Seasonal edit</Link>
              <Link to="/shop?category=Bags">Top-handle bags</Link>
            </nav>
          </div>
        </div>
        <div className="mobile-drawer__footer">
          <Link to="/contact">Customer care</Link>
          <Link to="/shipping">Shipping & returns</Link>
          <span>Ship to United States · English</span>
        </div>
      </aside>

      <div
        className={`search-drawer ${searchOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Search products"
      >
        <div className="search-drawer__header">
          {logo}
          <button
            className="icon-button"
            onClick={() => setSearchOpen(false)}
            aria-label="Close search"
          >
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSearch}>
          <FiSearch aria-hidden="true" />
          <input
            ref={searchInput}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="What are you looking for?"
            aria-label="Search catalog"
          />
          <button type="submit">Search</button>
        </form>
        <div className="search-suggestions">
          <p>POPULAR SEARCHES</p>
          {["New collection", "Dresses", "Cardigans", "Bags"].map((item) => (
            <button
              key={item}
              onClick={() => {
                setQuery(item);
                navigate(`/shop?q=${encodeURIComponent(item)}`);
              }}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {cartOpen && (
        <>
          <div
            className="mini-cart-overlay header-cart-overlay is-open"
            onClick={() => setCartOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="mini-cart header-mini-cart is-open"
            aria-label="Shopping cart"
            aria-live="polite"
          >
            <header>
              <strong>Shopping cart ({cartCount})</strong>
              <button
                onClick={() => setCartOpen(false)}
                aria-label="Close cart"
              >
                <FiX />
              </button>
            </header>
            {detailedCart.length ? (
              <div className="header-mini-cart__items">
                {detailedCart.slice(0, 3).map((item) => (
                  <article
                    className="mini-cart__item"
                    key={`${item.id}-${item.size}-${item.color}`}
                  >
                    <img src={item.product.image} alt="" />
                    <div>
                      <Link to={`/shopDetails/${item.id}`}>
                        <strong>{item.product.name}</strong>
                      </Link>
                      <span>
                        {item.color} · {item.size} · Qty {item.quantity}
                      </span>
                      <span>
                        {formatPrice(
                          Number(item.product.price) * item.quantity,
                        )}
                      </span>
                      <button
                        className="header-mini-cart__remove"
                        onClick={() =>
                          removeFromCart(item.id, item.size, item.color)
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="header-mini-cart__empty">
                <p>Your cart is empty.</p>
                <Link to="/shop">Continue shopping</Link>
              </div>
            )}
            <footer>
              {detailedCart.length > 0 && (
                <>
                  <div className="header-mini-cart__subtotal">
                    <span>Subtotal</span>
                    <strong>{formatPrice(cartSubtotal)}</strong>
                  </div>
                  <Link className="button" to="/cartpage">
                    View cart
                  </Link>
                </>
              )}
              <button onClick={() => setCartOpen(false)}>
                Continue shopping
              </button>
            </footer>
          </aside>
        </>
      )}
    </>
  );
}

export default Header;
